import { CalendarDate } from '@internationalized/date';
import { config } from '@rematter/config';
import { UsageMetricsSortOption } from '@rematter/frontend-lib-api-client';
import { maybe } from '@rematter/ui-web';
import { atom } from 'jotai';

import { systemUsageMetricsQueryAtom } from '../_graphql/querySystemUsageMetrics';

import { atomFromQueryString } from '~/shared/state';

export const $usageOffset = atomFromQueryString.number('offset', 0);
export const $usageLimit = atomFromQueryString.number('limit', config.pageSize);
export const $usageSortBy =
  atomFromQueryString.string<UsageMetricsSortOption>('sortBy');

const $organizationIdsFilterRaw = atomFromQueryString.string('organizationIds');

export const $organizationIdsFilter = atom(
  (get) => {
    const raw = get($organizationIdsFilterRaw);
    if (!raw) return undefined;
    return raw.split(',').filter(Boolean);
  },
  (get, set, value: string[] | undefined) => {
    set($organizationIdsFilterRaw, value?.join(',') || undefined);
  },
);

export const $modelProviderFilter = atomFromQueryString.string('modelProvider');
export const $modelFilter = atomFromQueryString.string('model');
export const $dateRange = atomFromQueryString.dateRange();

export const $remoteSystemUsageMetrics: ReturnType<
  typeof systemUsageMetricsQueryAtom
> = systemUsageMetricsQueryAtom((get) => {
  const organizationIds = get($organizationIdsFilter);
  const modelProvider = get($modelProviderFilter);
  const model = get($modelFilter);
  const dateRange = get($dateRange);

  return {
    variables: {
      filter: {
        organizationIds:
          organizationIds && organizationIds.length > 0
            ? organizationIds
            : undefined,
        modelProvider,
        model,
        dateFrom: dateRange?.start as CalendarDate,
        dateTo: dateRange?.end as CalendarDate,
      },
      sorting: maybe(get($usageSortBy), (sortBy) => [sortBy]),
      pagination: maybe(get($usageLimit), (limit) => ({
        limit,
        offset: get($usageOffset) ?? 0,
      })),
    },
  };
});

export const $usageMetrics = atom((get) => {
  const data = get($remoteSystemUsageMetrics);
  return data.data?.entries ?? [];
});

export const $usageTotal = atom((get) => {
  const data = get($remoteSystemUsageMetrics);
  return data.data?.total ?? 0;
});

export const $isUsageLoading = atom((get) => {
  const data = get($remoteSystemUsageMetrics);
  return data.isLoading;
});

export const $usageError = atom((get) => {
  const data = get($remoteSystemUsageMetrics);
  return data.error ?? null;
});
